import { createSelector } from '@reduxjs/toolkit';

import { parseMedia } from '../models/media';
import type { RootState } from '../reducers';

const IMAGE = 'Image';
const SPHERE = 'Sphere';
const EMBEDDED = 'Embedded';
const VIDEO = 'Video';

const getSearchDocument = createSelector(
  (state: RootState) => state.item.current,
  (documentList) =>
    documentList && documentList.documents && documentList.documents.length === 1
      ? documentList.documents[0]
      : null,
);

const getAdvertisementMedia = createSelector(getSearchDocument, (item) => {
  if (!item || !item.media || !item.media.length) return [];

  return item.media.map((media) => parseMedia(media));
});

export const getFirstMedia = createSelector(getAdvertisementMedia, (mediaList) => {
  if (!mediaList.length) return null;

  return mediaList[0].files[0].url;
});

const getImages = createSelector(getAdvertisementMedia, (mediaList) =>
  mediaList.filter((media) => media.files.length === 1 && media.type === IMAGE),
);

const get360Images = createSelector(getAdvertisementMedia, (mediaList) =>
  mediaList
    .filter((media) => media.files.length > 1 || media.type === '360')
    .map((media) => ({
      ...media,
      files: media.files.sort((a, b) => a.sortOrder - b.sortOrder),
    })),
);

const getSphereImages = createSelector(getAdvertisementMedia, (mediaList) =>
  mediaList.filter((media) => media.type === SPHERE),
);

const getVideos = createSelector(getAdvertisementMedia, (mediaList) =>
  mediaList.filter((media) => media.type === VIDEO),
);

const getEmbeddedVideos = createSelector(getAdvertisementMedia, (mediaList) =>
  mediaList.filter((media) => media.type === EMBEDDED),
);

export const getMedia = createSelector(
  getEmbeddedVideos,
  getVideos,
  getImages,
  get360Images,
  getSphereImages,
  (embedded, videos, images, threesixtys, spheres) =>
    [...embedded, ...videos, ...images, ...threesixtys, ...spheres].map((media, index) => ({
      ...media,
      sortOrder: index + 1,
    })),
);

const getFirstImageIndex = createSelector(getMedia, (mediaList) =>
  mediaList.findIndex((media) => media.files.length === 1 && media.type === IMAGE),
);

const getFirst360Index = createSelector(getMedia, (mediaList) =>
  mediaList.findIndex((media) => media.files.length > 1),
);

const getFirstSphereIndex = createSelector(getMedia, (mediaList) =>
  mediaList.findIndex((media) => media.type === SPHERE),
);

const getFirstEmbedIndex = createSelector(getMedia, (mediaList) =>
  mediaList.findIndex((media) => media.type === EMBEDDED),
);

const getFirstVideoIndex = createSelector(getMedia, (mediaList) =>
  mediaList.findIndex((media) => media.type === VIDEO),
);

export const getControlMenu = createSelector(
  getFirstImageIndex,
  getFirst360Index,
  getFirstSphereIndex,
  getFirstEmbedIndex,
  getFirstVideoIndex,
  (image, threesixty, sphere, embed, video) => ({
    image,
    threesixtyAndSphere: threesixty > -1 ? threesixty : sphere,
    embedAndVideo: embed > -1 ? embed : video,
  }),
);
